import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  ShieldCheck,
  Globe,
  Zap,
  Target,
  Heart
} from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

export default function AboutPage() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stats = [
    { label: "Active Users", value: "50K+", icon: Users },
    { label: "Verified Sellers", value: "2K+", icon: ShieldCheck },
    { label: "Cities Covered", value: "15+", icon: Globe },
    { label: "Items Sold", value: "100K+", icon: Zap },
  ];

  const values = [
    {
      title: "Trust & Safety",
      description: "We prioritize the security of every transaction with our verified seller program.",
      icon: ShieldCheck,
      color: "text-green-500"
    },
    {
      title: "Our Mission",
      description: "To create the most seamless and innovative marketplace for all Ethiopians.",
      icon: Target,
      color: "text-blue-500"
    },
    {
      title: "Community First",
      description: "We're building more than just a platform; we're building a community of trust.",
      icon: Heart,
      color: "text-red-500"
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-gray-50 text-white">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-900 to-green-900/30 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.h1
              {...fadeIn}
              className="text-5xl md:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-900 to-emerald-400"
            >
              About HuluOrder
            </motion.h1>
            <motion.p
              {...fadeIn}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed"
            >
              Ethiopia's leading marketplace where trust meets convenience.
              Connecting thousands of buyers and sellers every single day.
            </motion.p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-sm text-center border border-gray-100"
                >
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                  <p className="text-gray-500 font-medium">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Story / Content Section */}
        <section className="py-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold text-gray-900">Our Story</h2>
              <div className="w-20 h-1.5 bg-green-500 rounded-full"></div>
              <p className="text-lg text-gray-600 leading-relaxed">
                Founded with a vision to revolutionize the Ethiopian e-commerce landscape,
                HuluOrder started as a simple idea: make buying and selling accessible to everyone,
                everywhere.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Today, we are proud to be the platform of choice for thousands. Whether you're
                looking for the latest electronics, fashion trends, or home essentials,
                HuluOrder provides a secure environment for your commerce.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop"
                alt="Our Team"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 bg-gray-50 text-white relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-green-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>

          <div className="max-w-7xl mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-gray-900">Why Choose Us?</h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                We're committed to delivering the best experience for our community.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {values.map((value, i) => (
                <motion.div
                  key={i}
                  whileHover={{ y: -10 }}
                  className="p-8 rounded-2xl bg-gray-800 border border-gray-700 backdrop-blur-sm"
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center mb-6 shadow-xl`}>
                    <value.icon className={`w-8 h-8 ${value.color}`} />
                  </div>
                  <h3 className="text-xl font-bold mb-4">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
